// Filtering helpers for the map: ship type, era / year of loss, photos, and "needs work".
// Pure functions only (no React), so they are easy to test.

// KEEP IN SYNC with COMPLETE_MIN_DESCRIPTION in functions/_shared/points.js:
// a description shorter than this isn't "real", so the wreck counts as needing work.
export const NEEDS_WORK_MIN_DESCRIPTION = 100;

const known = (v) => v !== undefined && v !== null && String(v).trim() !== "" && String(v).toLowerCase() !== "unknown";

/* ---------- year of loss ---------- */

// Ship dates are free text ("1912", "10 Nov 1975", "April 15, 1912", "c. 1890", "1941–1945").
// We use the first 4-digit year we can find, or null when there isn't one.
export function parseYear(date, maxYear = new Date().getFullYear() + 1) {
  if (!known(date)) return null;
  const m = String(date).match(/(?<!\d)(1[0-9]{3}|20[0-9]{2})(?!\d)/);
  if (!m) return null;
  const y = Number(m[1]);
  return y >= 1000 && y <= maxYear ? y : null;
}

export const shipYear = (ship) => parseYear(ship?.date);

export const ERAS = [
  { id: "sail", label: "Age of sail", sub: "to 1849", from: null, to: 1849 },
  { id: "steam", label: "Steam era", sub: "1850–1913", from: 1850, to: 1913 },
  { id: "ww1", label: "WWI", sub: "1914–1918", from: 1914, to: 1918 },
  { id: "interwar", label: "Interwar", sub: "1919–1938", from: 1919, to: 1938 },
  { id: "ww2", label: "WWII", sub: "1939–1945", from: 1939, to: 1945 },
  { id: "modern", label: "Postwar", sub: "1946 on", from: 1946, to: null },
];

/* ---------- ship type ---------- */

// Types are free text ("Iron screw steamer", "Tribal-class destroyer"), so we group them into a
// handful of categories. The first rule that matches wins, so order matters.
export const TYPE_CATEGORIES = [
  { id: "warship", label: "Warship", re: /war ?ship|destroyer|cruiser|battle|frigate|corvette|gunboat|carrier|monitor|dreadnought|minesweeper|navy|naval/i },
  { id: "submarine", label: "Submarine", re: /submarine|u-boat|\bsub\b/i },
  { id: "tanker", label: "Tanker", re: /tanker/i },
  { id: "passenger", label: "Passenger / ferry", re: /passenger|ferry|liner|ocean liner|cruise/i },
  { id: "fishing", label: "Fishing vessel", re: /fish|trawler|whaler|whaling/i },
  { id: "tug", label: "Tug", re: /\btug|tow ?boat/i },
  { id: "yacht", label: "Yacht", re: /yacht|pleasure/i },
  { id: "sailing", label: "Sailing vessel", re: /sail|sloop|schooner|ketch|cutter|brig|barque|bark\b|clipper|yawl|lugger|windjammer|carvel/i },
  { id: "barge", label: "Barge", re: /barge|lighter|scow/i },
  { id: "steamer", label: "Steamer", re: /steam|paddle|screw/i },
  { id: "cargo", label: "Cargo ship", re: /cargo|freight|collier|bulk|container|merchant|coaster|coastal/i },
];

export const OTHER_TYPE = { id: "other", label: "Other" };
export const UNKNOWN_TYPE = { id: "unknown", label: "Type not given" };

export function typeCategory(type) {
  if (!known(type)) return UNKNOWN_TYPE.id;
  const t = String(type);
  for (const c of TYPE_CATEGORIES) if (c.re.test(t)) return c.id;
  return OTHER_TYPE.id;
}

export const typeLabel = (id) =>
  [...TYPE_CATEGORIES, OTHER_TYPE, UNKNOWN_TYPE].find((c) => c.id === id)?.label || id;

/* ---------- needs work ---------- */

// Why a wreck "needs work": [] means it's in good shape. Same rule the points bonus uses.
export function needsWorkReasons(ship) {
  const reasons = [];
  if (!Array.isArray(ship?.images) || ship.images.length === 0) reasons.push("no photo");
  if (String(ship?.description || "").trim().length < NEEDS_WORK_MIN_DESCRIPTION) reasons.push("short description");
  return reasons;
}
export const needsWork = (ship) => needsWorkReasons(ship).length > 0;

/* ---------- filters ---------- */

// from / to are years (null = open ended). includeUndated only matters while a year range is set.
export const DEFAULT_FILTERS = Object.freeze({
  types: [],
  from: null,
  to: null,
  includeUndated: false,
  hasPhotos: false,
  needsWork: false,
});

export const hasYearRange = (f) => f.from !== null || f.to !== null;

export function filtersActive(f) {
  return f.types.length > 0 || hasYearRange(f) || f.hasPhotos || f.needsWork;
}

export function matchesFilters(ship, f) {
  if (f.types.length && !f.types.includes(typeCategory(ship.type))) return false;

  if (hasYearRange(f)) {
    const y = shipYear(ship);
    if (y === null) {
      if (!f.includeUndated) return false;
    } else if ((f.from !== null && y < f.from) || (f.to !== null && y > f.to)) {
      return false;
    }
  }

  if (f.hasPhotos && !(Array.isArray(ship.images) && ship.images.length > 0)) return false;
  if (f.needsWork && !needsWork(ship)) return false;
  return true;
}

export const filterShips = (ships, f) => (filtersActive(f) ? ships.filter((s) => matchesFilters(s, f)) : ships);

// Slider limits taken from the data: first/last year of loss, rounded out to whole decades.
export function yearBounds(ships, now = new Date().getFullYear()) {
  let min = Infinity;
  let max = -Infinity;
  for (const s of ships) {
    const y = shipYear(s);
    if (y === null) continue;
    if (y < min) min = y;
    if (y > max) max = y;
  }
  if (min === Infinity || min === max) return null; // fewer than two distinct years: a slider is pointless
  return { min: Math.floor(min / 10) * 10, max: Math.min(Math.ceil(max / 10) * 10, now) };
}

// Bars for the timeline: [{ from, to, count }] with at most `maxBars` buckets.
export function yearHistogram(ships, bounds, maxBars = 48) {
  if (!bounds) return [];
  const span = bounds.max - bounds.min + 1;
  const step = Math.max(1, Math.ceil(span / maxBars));
  const bars = [];
  for (let from = bounds.min; from <= bounds.max; from += step) {
    bars.push({ from, to: Math.min(from + step - 1, bounds.max), count: 0 });
  }
  for (const s of ships) {
    const y = shipYear(s);
    if (y === null || y < bounds.min || y > bounds.max) continue;
    bars[Math.min(Math.floor((y - bounds.min) / step), bars.length - 1)].count++;
  }
  return bars;
}
